import React, { Component } from "react";
import PropTypes from "prop-types";
import LineSeries from "./LineSeries";
import StaticAreaSeries from "./StaticAreaSeries";
import StraightLine from "./StraightLine";
import SVGComponent from "./SVGComponent";
import { strokeDashTypes } from "../utils";
import {
  extractThresholdValue,
  extractThresholdLine,
  clipToBand,
  clipOutsideBand,
  clipAboveOverBought,
  clipBelowOverSold,
  bandClipRects,
} from "./oscillatorThresholds";

class RelativeVolatilitySeries extends Component {
  static yExtents(datum, index, allData, indicator) {
    // RelativeVolatility is always in 0-100 range like RSI
    return [0, 100];
  }

  constructor(props) {
    super(props);
    this.renderClip = this.renderClip.bind(this);
    this.topAndBottomClip = this.topAndBottomClip.bind(this);
    this.mainClip = this.mainClip.bind(this);
    this.overBoughtClip = this.overBoughtClip.bind(this);
    this.overSoldClip = this.overSoldClip.bind(this);

    const id1 = String(Math.round(Math.random() * 10000 * 10000));
    this.clipPathId1 = `relvol-clip-${id1}`;

    const id2 = String(Math.round(Math.random() * 10000 * 10000));
    this.clipPathId2 = `relvol-clip-${id2}`;

    const id3 = String(Math.round(Math.random() * 10000 * 10000));
    this.clipPathId3 = `relvol-clip-overbought-${id3}`;

    const id4 = String(Math.round(Math.random() * 10000 * 10000));
    this.clipPathId4 = `relvol-clip-oversold-${id4}`;
  }

  topAndBottomClip(ctx, moreProps) {
    const { overSold, overBought } = this.props;
    clipToBand(
      ctx,
      moreProps,
      extractThresholdValue(overBought, 70),
      extractThresholdValue(overSold, 30),
    );
  }

  mainClip(ctx, moreProps) {
    const { overSold, overBought } = this.props;
    clipOutsideBand(
      ctx,
      moreProps,
      extractThresholdValue(overBought, 70),
      extractThresholdValue(overSold, 30),
    );
  }

  overBoughtClip(ctx, moreProps) {
    const { overBought } = this.props;
    clipAboveOverBought(ctx, moreProps, extractThresholdValue(overBought, 70));
  }

  overSoldClip(ctx, moreProps) {
    const { overSold } = this.props;
    clipBelowOverSold(ctx, moreProps, extractThresholdValue(overSold, 30));
  }

  renderClip(moreProps) {
    const { overSold, overBought } = this.props;
    const { overBoughtY, overSoldY, width, height } = bandClipRects(
      moreProps,
      extractThresholdValue(overBought, 70),
      extractThresholdValue(overSold, 30),
    );

    return (
      <defs>
        <clipPath id={this.clipPathId1}>
          <rect
            x={0}
            y={overBoughtY}
            width={width}
            height={overSoldY - overBoughtY}
          />
        </clipPath>
        <clipPath id={this.clipPathId2}>
          <rect x={0} y={0} width={width} height={overBoughtY} />
          <rect x={0} y={overSoldY} width={width} height={height - overSoldY} />
        </clipPath>
        <clipPath id={this.clipPathId3}>
          <rect x={0} y={0} width={width} height={overBoughtY} />
        </clipPath>
        <clipPath id={this.clipPathId4}>
          <rect x={0} y={overSoldY} width={width} height={height - overSoldY} />
        </clipPath>
      </defs>
    );
  }

  render() {
    const { className, stroke, onContextMenu } = this.props;
    const { yAccessor } = this.props;
    const { overSold, middle, overBought } = this.props;

    const overBoughtData = extractThresholdLine(overBought, 70, {
      color: "#4A90E2",
      dash: "Dash",
      width: 1,
      opacity: 1,
    });
    const overSoldData = extractThresholdLine(overSold, 30, {
      color: "#E57373",
      dash: "Dash",
      width: 1,
      opacity: 1,
    });
    const middleData = middle
      ? extractThresholdLine(middle, 50, {
          color: "#8795A1",
          dash: "Dash",
          width: 1,
          opacity: 1,
        })
      : null;

    const relVolStroke = stroke.line ? stroke.line : stroke;

    const style1 = { clipPath: `url(#${this.clipPathId1})` };
    const style2 = { clipPath: `url(#${this.clipPathId2})` };
    const style3 = { clipPath: `url(#${this.clipPathId3})` };
    const style4 = { clipPath: `url(#${this.clipPathId4})` };

    // Use overbought/oversold threshold line colors for the fills
    const overBoughtFillColor =
      (overBoughtData &&
        overBoughtData.stroke &&
        overBoughtData.stroke.color) ||
      "#4A90E2";
    const overSoldFillColor =
      (overSoldData && overSoldData.stroke && overSoldData.stroke.color) ||
      "#E57373";

    return (
      <g className={className}>
        <SVGComponent>{this.renderClip}</SVGComponent>
        <StraightLine
          stroke={overSoldData.stroke}
          yValue={overSoldData.value}
          onContextMenu={onContextMenu}
        />
        {middleData && (
          <StraightLine
            stroke={middleData.stroke}
            yValue={middleData.value}
            onContextMenu={onContextMenu}
          />
        )}
        <StraightLine
          stroke={overBoughtData.stroke}
          yValue={overBoughtData.value}
          onContextMenu={onContextMenu}
        />
        {/* Fill area above overbought line - only shows when indicator is in overbought zone */}
        <StaticAreaSeries
          style={style3}
          canvasClip={this.overBoughtClip}
          className={className}
          yAccessor={yAccessor}
          fill={overBoughtFillColor}
          opacity={0.3}
          base={(yScale) => yScale(overBoughtData.value)}
        />
        {/* Fill area below oversold line - only shows when indicator is in oversold zone */}
        <StaticAreaSeries
          style={style4}
          canvasClip={this.overSoldClip}
          className={className}
          yAccessor={yAccessor}
          fill={overSoldFillColor}
          opacity={0.3}
          base={(yScale) => yScale(overSoldData.value)}
        />
        <LineSeries
          style={style1}
          canvasClip={this.topAndBottomClip}
          className={className}
          yAccessor={yAccessor}
          stroke={relVolStroke}
          onContextMenu={onContextMenu}
        />
        <LineSeries
          style={style2}
          canvasClip={this.mainClip}
          className={className}
          yAccessor={yAccessor}
          stroke={relVolStroke}
          onContextMenu={onContextMenu}
        />
      </g>
    );
  }
}

RelativeVolatilitySeries.propTypes = {
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  stroke: PropTypes.oneOfType([
    PropTypes.shape({
      color: PropTypes.string,
      opacity: PropTypes.number,
      width: PropTypes.number,
      dash: PropTypes.oneOf(strokeDashTypes),
    }),
    PropTypes.shape({
      line: PropTypes.shape({
        color: PropTypes.string,
        opacity: PropTypes.number,
        width: PropTypes.number,
        dash: PropTypes.oneOf(strokeDashTypes),
      }),
    }),
  ]).isRequired,
  overSold: PropTypes.number.isRequired,
  middle: PropTypes.number,
  overBought: PropTypes.number.isRequired,
  onContextMenu: PropTypes.func,
};

RelativeVolatilitySeries.defaultProps = {
  className: "react-stockcharts-relative-volatility-series",
  stroke: { color: "#9C27B0", opacity: 1, width: 1, dash: "Solid" },
  overSold: 30,
  overBought: 70,
  middle: 50,
};

export default RelativeVolatilitySeries;
